/**
 * Registering the service worker, and getting an installed copy onto a new release.
 *
 * An installed application is not reloaded the way a tab is: it is opened, left running for days,
 * and never hard-refreshed. So a new release has to announce itself, and the operator has to be
 * the one who accepts it — swapping the code under a half-written journal entry would be worse
 * than showing yesterday's build for another minute.
 */

type UpdateListener = (updateReady: boolean) => void;

const listeners = new Set<UpdateListener>();
let waitingWorker: ServiceWorker | null = null;
let registration: ServiceWorkerRegistration | null = null;
let reloading = false;

/** How often a long-lived window asks whether a new release has been deployed. */
const UPDATE_CHECK_INTERVAL_MS = 60 * 60_000;

function announce(): void {
  for (const listener of listeners) listener(waitingWorker !== null);
}

export function subscribeToUpdates(listener: UpdateListener): () => void {
  listeners.add(listener);
  listener(waitingWorker !== null);
  return () => {
    listeners.delete(listener);
  };
}

export function updateAvailable(): boolean {
  return waitingWorker !== null;
}

function watch(worker: ServiceWorker | null): void {
  if (worker === null) return;
  const check = () => {
    // `installed` with a controller already in place means a *replacement* is ready, rather than
    // the very first installation — which needs no announcement at all.
    if (worker.state === 'installed' && navigator.serviceWorker.controller !== null) {
      waitingWorker = worker;
      announce();
    }
  };
  check();
  worker.addEventListener('statechange', check);
}

/**
 * Registers the worker. Called only from a production bundle: under the dev server the module
 * graph is rewritten on every keystroke, and a caching worker in front of that is a trap.
 */
export function registerServiceWorker(): void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    // The worker the operator accepted has taken over; the window reloads once onto the new code.
    if (!reloading) return;
    window.location.reload();
  });

  void navigator.serviceWorker
    .register('/sw.js', { scope: '/', updateViaCache: 'none' })
    .then((current) => {
      registration = current;
      watch(current.waiting);
      watch(current.installing);
      current.addEventListener('updatefound', () => watch(current.installing));

      window.setInterval(() => void current.update().catch(() => undefined), UPDATE_CHECK_INTERVAL_MS);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') void current.update().catch(() => undefined);
      });
    })
    .catch(() => {
      // An unregisterable worker costs the offline shell, not the application: everything else
      // keeps working against the network.
    });
}

/** Accepts a waiting release: the worker takes over and the window reloads onto it. */
export function applyUpdate(): void {
  if (waitingWorker === null) return;
  reloading = true;
  waitingWorker.postMessage({ type: 'varza:skip-waiting' });
}

/** Asks the worker to look for a new release now. */
export async function checkForUpdate(): Promise<void> {
  await registration?.update().catch(() => undefined);
}
